import { useEffect, useState } from 'react';
import { z } from 'zod';

import { showErrorToUser, useForm } from '@pletly/ui';
import { useAuth } from '../../../context';
import { rememberEmail } from '../../../services/rememberLogin';
import { usernameSchema, passwordSchema } from '../../../consts';

const formSchema = z.object({
	email: z.string().email(),
	username: usernameSchema,
	password: passwordSchema,
	newsletter: z.boolean().default(false),
	registerCode: z.string().optional(),
});

export const useRegister = () => {
	const [isSending, setIsSending] = useState(false);

	const formMethods = useForm(formSchema);

	const { register: signUp, abortRegister } = useAuth();

	const handleRegister: Parameters<typeof formMethods.handleSubmit>['0'] = async ({
		email,
		password,
		username,
		newsletter,
		registerCode,
	}) => {
		if (isSending) {
			return;
		}
		setIsSending(true);

		try {
			const trimmedEmail = email.trim();
			await rememberEmail(trimmedEmail);

			await signUp({
				email: trimmedEmail,
				password,
				username,
				newsletter,
				registerCode,
			});
		} catch (error) {
			showErrorToUser(error as Error);
		} finally {
			setIsSending(false);
		}
	};

	const onSubmit = formMethods.handleSubmit(handleRegister);

	useEffect(() => {
		return () => {
			void abortRegister();
		};
	}, [abortRegister]);

	return { isSending, onSubmit, formMethods };
};
